import React from "react";

import { getDuration, getDurationString } from "~/lib/date";
import { shortCommit } from "~/lib/commits";
import { track } from "~/lib/segmentAnalytics";
import BuildMessage from "~/components/build/Message";
import BuildState from "~/components/icons/BuildState";
import Duration from "~/components/shared/Duration";
import FriendlyTime from "~/components/shared/FriendlyTime";
import Icon from "~/components/shared/Icon";
import UserAvatar from "~/components/shared/UserAvatar";
import {
  getDurationPrefix,
  getDurationSuffix,
  getDurationValues,
} from "~/components/build/DurationNext";

import Steps from "~/components/build/Header/pipeline/Steps";
import { Build } from "~/components/build/Show/lib/types";

function BuildMeta({ build }: { build: Build }) {
  const sha = shortCommit(build.commitId, build.commitShortLength);
  const seperator = <span>&middot;</span>;

  return (
    <div className="build-row-meta gapx1.5 charcoal-600 flex flex-wrap items-center text-xs leading-5">
      <span className="gap1 flex items-center">
        <UserAvatar
          user={{
            name: build.authorName,
            avatar: { url: build.authorAvatar || "" },
          }}
          style={{ width: 20, height: 20 }}
          suppressAltText={true}
        />
        {build.authorName}
      </span>

      {seperator}

      <span className="gap0.5 flex items-center">
        {build.mergeQueueBaseBranch ? (
          <a
            data-turbo="true"
            href={build.mergeQueuePath}
            className="color-inherit hover-color-inherit flex items-center"
          >
            <Icon icon="custom/outline/14x/git-merge-queue" style={{ height: 14, width: 14 }} />
            {build.mergeQueueBaseBranch}
          </a>
        ) : (
          <a
            data-turbo="true"
            href={build.branchPath}
            className="color-inherit hover-color-inherit flex items-center"
          >
            <Icon icon="custom/outline/14x/branch" style={{ height: 14, width: 14 }} />
            {build.branchName}
          </a>
        )}

        <Icon icon="custom/outline/20x/slash" style={{ height: 14, width: 14 }} />

        <a
          href={build.commitUrl}
          target="_blank"
          className="monospace color-inherit hover-color-inherit flex items-center"
          rel="noreferrer"
          style={{ fontSize: 12 }}
        >
          <Icon
            icon={build.project.provider.frontendIcon}
            className="mr-1"
            style={{ height: 14, width: 14 }}
          />
          {sha}
        </a>
      </span>

      {seperator}

      <span>
        Created <FriendlyTime value={build.createdAt} />
      </span>
    </div>
  );
}

function getTooltip(build: Build) {
  const prefix = getDurationPrefix(build) || "";
  const suffix = getDurationSuffix(build) || "";

  const durationValues = getDurationValues(build);
  const durationString = durationValues
    ? // @ts-expect-error - TS2345 - Argument of type 'Duration' is not assignable to parameter of type 'number'.
      getDurationString(getDuration(durationValues.from, durationValues.to))
    : "";

  return `${prefix} ${durationString} ${suffix}`.trim();
}

const BuildRow = ({
  build,
  setBuildData,
  reloadBuild,
  showStepsToggle,
}: {
  build: Build;
  setBuildData: (build: Build) => void;
  reloadBuild: () => void;
  showStepsToggle: boolean;
}) => {
  const durationValues = getDurationValues(build);
  const tooltip = getTooltip(build);

  const buildState = build.state === "started" ? "RUNNING" : build.state.toUpperCase();

  const [showSteps, setShowSteps] = React.useState(false);

  function toggleSteps(event: React.MouseEvent<HTMLButtonElement>) {
    event.preventDefault();
    event.currentTarget.blur();

    track("Builds List Toggle Steps Clicked", {
      action: showSteps ? "Hide" : "Show",
      text: showSteps ? `Hide steps for #${build.number}` : `Show steps for #${build.number}`,
      source: "Build Row",
    });

    setShowSteps(!showSteps);
  }

  return (
    <div className="build-row border-gray border-b antialiased">
      <div className="build-row-header gapx2.5 gapy0.5 px-4 py-2">
        <div
          className="build-row-status flex cursor-help justify-center self-center"
          title={tooltip}
        >
          <BuildState.Regular
            state={buildState}
            blockedState={build.blockedState && build.blockedState.toUpperCase()}
            className="block"
          />
        </div>
        <div
          className="build-row-duration charcoal-600 -mx-2 cursor-help whitespace-nowrap text-center text-xs leading-5 tracking-tight"
          title={tooltip}
        >
          {durationValues ? (
            <Duration.Micro {...durationValues} />
          ) : (
            <span className="gray-800">—</span>
          )}
        </div>

        <a
          className="build-row-message min-width-0 group inline-flex self-center hover:no-underline"
          href={build.path}
        >
          <span className="charcoal-900 min-w-0 max-w-full truncate font-semibold group-hover:underline">
            <BuildMessage message={build.message} />
          </span>

          <span className="gray-800 ml-1 group-hover:no-underline">#{build.number}</span>
        </a>

        <BuildMeta build={build} />

        {showStepsToggle && build.jobs.length > 0 && (
          <div className="build-row-toggle-steps -mx0.5 self-center">
            <button
              onClick={toggleSteps}
              className="unstyled-button charcoal-300 hover-charcoal-800 p-1"
              aria-label={showSteps ? "Hide steps" : "Show steps"}
            >
              <span className="sr-only">
                {showSteps ? `Hide steps for #${build.number}` : `Show steps for #${build.number}`}
              </span>

              <Icon icon="heroicons/20/solid/chevron-up-down" style={{ height: 20, width: 20 }} />
            </button>
          </div>
        )}
      </div>

      {showSteps && (
        <div
          className="build-row-steps gap1 shadow-inset flex min-w-0 flex-auto flex-wrap border-t border-gray-400 bg-slate-100 px-4 py-2"
          style={{ paddingLeft: 49 }}
        >
          <Steps build={build} setBuildData={setBuildData} reloadBuild={reloadBuild} small={true} />
        </div>
      )}
    </div>
  );
};

export default BuildRow;
